/**
 * Where to go once a sign-in completes.
 *
 * The sign-in pages live in the public app, and a `redirect=` destination is
 * usually one of its own routes (`/authorize?…`), reached with the router's
 * `push`. But the storefront sends visitors to sign in from the management
 * app — `/Store/Account`, say — and that destination belongs to the *other*
 * SPA: pushed into the public app's router it would match nothing, or be
 * bounced to the dashboard by its "signed-in users belong in /Manage" guard.
 * Those destinations need a full page load instead.
 */

/**
 * Whether `path` is a page of the management app (`/Manage` or the `/Store`
 * root it also serves), and a same-origin one.
 *
 * `//host` is protocol-relative and would leave the origin, which is the open
 * redirect a `redirect=` parameter is otherwise an invitation to — so only a
 * path whose first segment is exactly one of the two roots qualifies.
 */
export function isManagementAppPath(path: string): boolean {
	return /^\/(Manage|Store)([/?#]|$)/.test(path);
}

/**
 * Sends the user to a post-sign-in destination: a page load into the
 * management app, or a route change within this one.
 *
 * @param destination - The `redirect=` path, already resolved by the caller.
 * @param pushRoute - The public app's router `push`, for its own routes.
 * @param navigate - Performs a full page load (a seam for tests; `window.location` cannot be observed).
 */
export function followPostLoginRedirect(
	destination: string,
	pushRoute: (path: string) => unknown,
	navigate: (url: string) => void = (url) => {
		window.location.href = url;
	}
): void {
	/* A page of the other SPA — only a page load reaches it. */
	if (isManagementAppPath(destination)) {
		navigate(destination);
		return;
	}
	pushRoute(destination);
}
